import { PropsWithChildren, useEffect, useRef } from "react";

import {
    useGetMySettingsQuery,
    usePutUserSettingsMutation,
} from "services/streamingApi/user.api";
import { useAppSelector } from "app/hooks";
import { useUserSettingColorScheme } from "./hooks/useUserSettingColorScheme";
import { userSelectors } from "./store/selectors";
import debounce from "@mui/utils/debounce";
import { unwrappableActionSnackbarNotificationDecorator } from "utils/unwrappableActionSnackbarNotificationDecorator";
import UserSettings from "./types/UserSettings";

export function UserSettingsLoader(props: PropsWithChildren) {
    // get state
    const userSettings = useAppSelector(userSelectors.selectUserSettings);
    const userSettingsRefreshIntervalInSeconds = useAppSelector(
        userSelectors.selectUserSettingsRefreshIntervalInSeconds,
    );
    const isSavingUserSettings = useAppSelector(
        userSelectors.selectIsSavingUserSettings,
    );
    const isDirtyUserSettings = useAppSelector(
        userSelectors.selectIsDirtyUserSettings,
    );
    const hasLoadedUserSettings = useAppSelector(
        userSelectors.selectHasLoadedUserSettings,
    );

    // upsert settings logic when user settings are changed
    const [putUserSettings] = usePutUserSettingsMutation();

    const debouncedUpsertUserSettings = useRef(
        debounce(
            (newUserSettings: UserSettings) =>
                unwrappableActionSnackbarNotificationDecorator(
                    putUserSettings({ userSettings: newUserSettings }),
                    "User settings saved",
                    "Error while saving user settings",
                ),
            500,
        ),
    );

    useEffect(() => {
        if (userSettings && isDirtyUserSettings) {
            debouncedUpsertUserSettings.current(userSettings);
        }
    }, [userSettings, isDirtyUserSettings]);

    // auto update settings for user when changed in different window
    useGetMySettingsQuery(undefined, {
        pollingInterval: 1000 * userSettingsRefreshIntervalInSeconds,
        skip: isSavingUserSettings,
    });

    // apply color scheme
    useUserSettingColorScheme();

    return hasLoadedUserSettings ? props.children : undefined;
}
